import { Component, ElementRef, HostBinding, Input, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, FormGroupDirective } from '@angular/forms';
import { MAT_MENU_DEFAULT_OPTIONS, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ReplaySubject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Member } from '@security/users/models/member';
import { AuthStorageService } from '@auth-n/state/auth-storage.service';
import { SearchInputComponent } from '@design/forms/search-input/search-input.component';
import { BodyMediumComponent } from '@design/typography/body-md/body-medium.component';
import { TestIdDirective } from '@design/core/test-id.directive';

/**
 * Material menu which presents the list of Organisations the user can select to work with.
 * Embedded in the {@link HeaderComponent}.
 */
@UntilDestroy()
@Component({
  selector: 'app-organisation-menu',
  templateUrl: './organisation-menu.component.html',
  styleUrls: ['../_context-menu.scss'],
  styles: ['::ng-deep .cdk-overlay-pane.org-menu-overlay { max-height: 75%;}'],
  providers: [
    FormGroupDirective,
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: {
        overlayPanelClass: 'org-menu-overlay'
      }
    }
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    SearchInputComponent,
    BodyMediumComponent,
    TestIdDirective
  ]
})
export class OrganisationMenuComponent implements OnInit, AfterViewInit {
  searchTerm: string = '';
  searchTermControl = new UntypedFormControl('');
  formGroup = new UntypedFormGroup({
    searchTerm: this.searchTermControl
  });
  filterSet$: ReplaySubject<Member[]> = new ReplaySubject<Member[]>(1);

  @ViewChild('searchInput') searchInput?: any;
  @ViewChild('orgOpener') orgOpener!: ElementRef;
  @ViewChild('orgOpener', { read: MatMenuTrigger }) menuTrigger!: MatMenuTrigger;

  @Input() activeMembership!: Member;
  @Input() membershipsWithAdminAccess!: Member[];

  constructor(private authStorageService: AuthStorageService) {}

  ngOnInit() {
    this.filterSet$.next(this.membershipsWithAdminAccess);

    this.searchTermControl.valueChanges.pipe(untilDestroyed(this)).subscribe((item) => {
      this.filterMyOptions(item);
    });
  }

  ngAfterViewInit() {
    if (!this.menuTrigger) return;
    this.menuTrigger.menuOpened.pipe(untilDestroyed(this)).subscribe(() => {
      this.scrollMenuToTop();
    });
  }

  changeOrg(membership: Member) {
    this.authStorageService.setOrganisation(membership.organisationId, membership.organisationName);
    window.location.href = '/';
  }

  focusSearch() {
    this.searchTermControl.setValue('');
    this.searchInput.elementRef.nativeElement.focus();
  }

  private scrollMenuToTop(): void {
    setTimeout(() => {
      const menuPanel = document.querySelector('.context-menu') as HTMLElement;
      if (menuPanel) {
        menuPanel.scrollTop = 0;
      }
    }, 10);
  }

  filterMyOptions(target: string) {
    if (!target) {
      this.filterSet$.next(this.membershipsWithAdminAccess.slice());
      return;
    } else {
      target = target.toLowerCase();
    }

    this.filterSet$.next(this.membershipsWithAdminAccess.filter((item) => item.organisationName.toLowerCase().indexOf(target) > -1));
  }
}
